import * as React from "react";
import {
  Locale,
  VISIBLE_LOCALES,
  detectInitialLocale,
  formatMoney,
  isLocaleContentReady,
  loadLocaleContent,
  t,
} from "@/lib/i18n";
import {
  MARKETS,
  MarketId,
  detectInitialMarket,
  isGiftDestinationActive,
  marketFromCountryCode,
} from "@/lib/markets";
import type { CountryCode } from "@/lib/shopify";
import { decodeCountryFromHeaders } from "@/lib/detectCountry";
import { setCartMarketContext, useCartStore } from "@/stores/cartStore";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { useStorefrontCurrencies } from "@/hooks/useStorefrontCurrencies";

export type MarketSource = "auto" | "manual" | "profile";

interface LocaleContextValue {
  locale: Locale;
  marketId: MarketId;
  countryCode: CountryCode;
  currency: string;
  currencyLabels: Record<string, string>;
  marketSource: MarketSource;
  /** True until the first detection round-trip completes. */
  marketReady: boolean;
  /** True when auto-detection has populated a country (used by the banner). */
  showDetectionBanner: boolean;
  dismissDetectionBanner: () => void;
  /** السوق الذي كشفه IP — قد يختلف عن marketId إن اختار المستخدم يدويّاً. */
  detectedMarketId: MarketId | null;
  /** true عندما detectedMarketId ≠ marketId ولم يُسكِته المستخدم في الجلسة. */
  showMismatchBanner: boolean;
  dismissMismatchBanner: () => void;
  setLocale: (l: Locale) => void;
  /** Manual override — persisted as `source: "manual"` (و في profiles إن المستخدم مسجّل دخول). */
  setMarket: (m: MarketId) => void;
  /** Re-run automatic IP detection, clearing any manual override. */
  resetMarketToAuto: () => Promise<void>;

  // ─── Gift override (مؤقّت — سعر سوق المستلم أثناء جلسة الإهداء) ───
  /**
   * سوق تسعير يختلف عن سوق العميل: سوق السلّة غير الفارغة المؤكَّد
   * (cartCountry) أو مسوّدة لسلّة فارغة. ليس دليلاً على حفظ هديّة.
   */
  giftMarketOverride: MarketId | null;
  /** فعّل سوق المستلم — يُغطّي marketId في القيم المشتقّة effective*. */
  setGiftMarketOverride: (m: MarketId) => void;
  /** يمسح المسوّدة فقط؛ سوق السلّة غير الفارغة لا يتغيّر إلّا بإعادة تسعير مؤكَّدة. */
  clearGiftMarketOverride: () => void;
  /** السوق الفعليّ المستخدم للأسعار = giftMarketOverride ?? marketId. */
  effectiveMarket: MarketId;
  effectiveCurrency: string;
  effectiveCountryCode: CountryCode;

  t: (key: string, vars?: Record<string, string>) => string;
  formatPrice: (amount: string | number, currencyCode: string) => string;
  dir: "ltr" | "rtl";
}

const LocaleContext = React.createContext<LocaleContextValue | null>(null);

const STORAGE_KEY = "lux-locale-v3";
// v2: انتقل التخزين من sessionStorage إلى localStorage — البانر إعلاميّ فقط،
// فلا داعي لتكراره كلّ جلسة. يعود فقط بعد ٩٠ يوماً أو عند تغيّر السوق المكتشَف.
const BANNER_KEY = "lux-detection-banner-shown-v2";
const BANNER_TTL_MS = 90 * 24 * 60 * 60 * 1000;
const MISMATCH_DISMISS_KEY = "lux-mismatch-dismissed";

type BannerSeen = { market: string; ts: number };

function readBannerSeen(): BannerSeen | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(BANNER_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<BannerSeen>;
    if (typeof parsed?.market !== "string" || typeof parsed?.ts !== "number") return null;
    return { market: parsed.market, ts: parsed.ts };
  } catch {
    return null;
  }
}

function writeBannerSeen(market: string) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(
      BANNER_KEY,
      JSON.stringify({ market, ts: Date.now() } satisfies BannerSeen),
    );
  } catch {
    /* ignore */
  }
}

/** يظهر البانر إن لم يُعرَض قبلاً، أو مضى ٩٠ يوماً، أو تغيّر السوق المكتشَف. */
function shouldShowDetectionBanner(market: string): boolean {
  const seen = readBannerSeen();
  if (!seen) return true;
  if (seen.market !== market) return true;
  return Date.now() - seen.ts > BANNER_TTL_MS;
}

interface PersistedState {
  locale?: Locale;
  market?: MarketId;
  source?: MarketSource;
}

function isValidMarketId(value: string | null | undefined): value is MarketId {
  return !!value && value in MARKETS;
}

function readPersistedAsync(): PersistedState {
  if (typeof window === "undefined") return {};
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as PersistedState) : {};
  } catch {
    return {};
  }
}

async function detectCountryFromApi() {
  const response = await fetch("/api/public/detect-country", {
    method: "GET",
    headers: { Accept: "application/json" },
  });

  if (!response.ok) {
    throw new Error(`detect-country failed: ${response.status}`);
  }

  return (await response.json()) as ReturnType<typeof decodeCountryFromHeaders>;
}

export function LocaleProvider({ children }: { children: React.ReactNode }) {
  const { user, loading: authLoading } = useAuth();
  const cartId = useCartStore((s) => s.cartId);
  const cartCountry = useCartStore((s) => s.cartCountry);
  const itemCount = useCartStore((s) => s.items.length);
  const [cartHydrated, setCartHydrated] = React.useState(false);
  React.useEffect(() => {
    let active = true;
    const start = useCartStore.persist.onHydrate(() => setCartHydrated(false));
    const finish = useCartStore.persist.onFinishHydration(() => setCartHydrated(true));
    if (useCartStore.persist.hasHydrated()) setCartHydrated(true);
    else {
      // A failed initial JSON read does not fire onFinishHydration. Await one
      // recovery attempt so an empty fallback cart cannot lock the market forever.
      const settled = () => { if (active) setCartHydrated(true); };
      void Promise.resolve().then(() => useCartStore.persist.rehydrate()).then(settled, settled);
    }
    return () => { active = false; start(); finish(); };
  }, []);

  // ─── أوّل render حتميّ: نفس القيم على السيرفر والعميل ───
  // أيّ اعتماد على localStorage/navigator وقت الـ initial state يكسر hydration
  // ويُعيد بناء الشجرة (ومعها Embla يبدأ بحسابات خاطئة).
  // التفضيلات تُطبَّق في useEffect بعد mount حيث يكون React "متّفقاً" مع DOM.
  const [locale, setLocaleState] = React.useState<Locale>("en");
  const [marketId, setMarketState] = React.useState<MarketId>("AE");
  const [marketSource, setMarketSource] = React.useState<MarketSource>("auto");
  const [persistedReady, setPersistedReady] = React.useState(false);
  const [profileReady, setProfileReady] = React.useState(false);
  const [showDetectionBanner, setShowDetectionBanner] = React.useState(false);
  const [detectedMarketId, setDetectedMarketId] = React.useState<MarketId | null>(null);
  const [mismatchDismissedFor, setMismatchDismissedFor] = React.useState<string | null>(() => {
    if (typeof window === "undefined") return null;
    try {
      return window.localStorage.getItem(MISMATCH_DISMISS_KEY);
    } catch {
      return null;
    }
  });

  // بعد mount: طبّق المحفوظ من localStorage فوراً (دون كسر hydration)
  React.useEffect(() => {
    const persisted = readPersistedAsync();
    // `?lang=ar|en` يأتي من تحويلات الروابط القديمة (‎/ar/…‎) فيفتح الصفحة
    // بلغة نتيجة البحث نفسها بدل الاعتماد على كشف IP/المتصفّح.
    let fromUrl: Locale | null = null;
    try {
      const raw = new URLSearchParams(window.location.search).get("lang");
      if (raw && VISIBLE_LOCALES.includes(raw as Locale)) fromUrl = raw as Locale;
    } catch {
      /* ignore malformed query strings */
    }
    const target =
      fromUrl ??
      (persisted.locale && VISIBLE_LOCALES.includes(persisted.locale)
        ? persisted.locale
        : detectInitialLocale());
    if (VISIBLE_LOCALES.includes(target)) {
      // نصوص اللغة غير الإنجليزيّة تُحمَّل قبل التبديل (chunk منفصل) لئلّا
      // تظهر مفاتيح ناقصة لجزء من الثانية.
      if (isLocaleContentReady(target)) setLocaleState(target);
      else void loadLocaleContent(target).then(() => setLocaleState(target));
    }



    if (persisted.source === "manual" && isValidMarketId(persisted.market)) {
      setMarketState(persisted.market);
      setMarketSource("manual");
      setPersistedReady(true);
      return;
    }
    if (isValidMarketId(persisted.market)) {
      setMarketState(persisted.market);
      setMarketSource("auto");
    }

    detectCountryFromApi()
      .then((res) => {
        const next = marketFromCountryCode(res.countryCode);
        setDetectedMarketId(next);
        setMarketState(next);
        setMarketSource("auto");
        if (shouldShowDetectionBanner(next)) {
          setShowDetectionBanner(true);
          writeBannerSeen(next);
        }
        setPersistedReady(true);
      })
      .catch(() => {
        setPersistedReady(true);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  // Sync with the signed-in user's `preferred_market` (highest priority).
  // - إذا الملفّ يحوي قيمة → نطبّقها على الـ context.
  // - إذا فارغة + لدينا قيمة manual في localStorage → نهجّرها إلى الملفّ بشفافيّة.
  React.useEffect(() => {
    if (authLoading) {
      setProfileReady(false);
      return;
    }

    if (!user) {
      setProfileReady(true);
      return;
    }

    let cancelled = false;
    setProfileReady(false);

    (async () => {
      try {
        const { data, error } = await supabase
          .from("profiles")
          .select("preferred_market")
          .eq("id", user.id)
          .maybeSingle();
        if (cancelled || error) return;

        const stored = (data?.preferred_market as string | null) ?? null;
        if (isValidMarketId(stored)) {
          setMarketState(stored);
          setMarketSource("profile");
          return;
        }

        // الملفّ فارغ → هل لدينا اختيار يدويّ في localStorage نهجّره؟
        let persisted: PersistedState = {};
        try {
          const raw = localStorage.getItem(STORAGE_KEY);
          if (raw) persisted = JSON.parse(raw);
        } catch {
          /* ignore */
        }
        if (persisted.source === "manual" && isValidMarketId(persisted.market)) {
          await supabase
            .from("profiles")
            .update({ preferred_market: persisted.market })
            .eq("id", user.id);
        }
      } catch (err) {
        console.warn("LocaleContext: profile market sync failed", err);
      } finally {
        if (!cancelled) {
          setProfileReady(true);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user, authLoading]);

  // Persist on every change.
  React.useEffect(() => {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ locale, market: marketId, source: marketSource } satisfies PersistedState),
      );
    } catch {
      /* ignore */
    }
    if (typeof document !== "undefined") {
      document.documentElement.lang = locale;
      document.documentElement.dir = locale === "ar" ? "rtl" : "ltr";
    }
    // باراميترات عامّة لأحداث ميتا (Custom Conversions حسب اللغة/السوق)
    import("@/lib/metaPixel")
      .then(({ setMetaTrackingContext }) =>
        setMetaTrackingContext({ language: locale, market: marketId }),
      )
      .catch(() => {});
  }, [locale, marketId, marketSource]);

  const market = MARKETS[marketId];
  const marketReady = persistedReady && profileReady && cartHydrated;

  // تبديل اللغة: نصوص اللغة الهدف تُحمَّل (chunk منفصل) قبل تغيير الحالة.
  const switchLocale = React.useCallback((next: Locale) => {
    if (isLocaleContentReady(next)) {
      setLocaleState(next);
      return;
    }
    void loadLocaleContent(next).then(() => setLocaleState(next));
  }, []);


  const setMarket = React.useCallback(
    (m: MarketId) => {
      setMarketState(m);
      setMarketSource("manual");
      setShowDetectionBanner(false);
      // sync to profile (fire-and-forget)
      if (user) {
        supabase
          .from("profiles")
          .update({ preferred_market: m })
          .eq("id", user.id)
          .then(({ error }) => {
            if (error) console.warn("LocaleContext: persist market failed", error);
          });
      }
    },
    [user],
  );

  const resetMarketToAuto = React.useCallback(async () => {
    try {
      const res = await detectCountryFromApi();
      setMarketState(marketFromCountryCode(res.countryCode));
    } catch {
      setMarketState(detectInitialMarket());
    }
    setMarketSource("auto");
    if (user) {
      supabase
        .from("profiles")
        .update({ preferred_market: null })
        .eq("id", user.id)
        .then(({ error }) => {
          if (error) console.warn("LocaleContext: clear market failed", error);
        });
    }
  }, [user]);

  const dismissDetectionBanner = React.useCallback(() => {
    setShowDetectionBanner(false);
    // تجديد الطابع الزمنيّ عند الإغلاق اليدويّ أيضاً.
    writeBannerSeen(detectedMarketId ?? marketId);
  }, [detectedMarketId, marketId]);

  // ─── Draft override + confirmed pricing market of the current nonempty cart ───
  const [draftGiftMarket, setGiftMarketOverrideState] =
    React.useState<MarketId | null>(null);
  const cartMarket = cartCountry ? marketFromCountryCode(cartCountry) : "ROW";
  // The nonempty cart's confirmed country (already persisted) is the pricing
  // context, independent of gift draft/save/removal. Changing it requires the
  // explicit serialized repricing transaction (switchCartMarketForGift).
  const cartPricingMarket = cartHydrated && cartId && itemCount > 0 &&
    cartMarket !== "ROW" && isGiftDestinationActive(cartMarket) &&
    MARKETS[cartMarket].countryCode === cartCountry
    ? cartMarket : null;
  const pricingMarket = cartPricingMarket ?? (itemCount > 0 ? null : draftGiftMarket);
  const giftMarketOverride = pricingMarket && pricingMarket !== marketId ? pricingMarket : null;
  React.useEffect(() => {
    // A nonempty cart owns the context; a draft must not outlive it as a stale override.
    if (cartPricingMarket && draftGiftMarket) setGiftMarketOverrideState(null);
  }, [cartPricingMarket, draftGiftMarket]);

  const setGiftMarketOverride = React.useCallback((m: MarketId) => {
    setGiftMarketOverrideState(m);
  }, []);
  const clearGiftMarketOverride = React.useCallback(() => {
    setGiftMarketOverrideState(null);
  }, []);

  const effectiveMarket: MarketId = giftMarketOverride ?? marketId;
  const effectiveMarketObj = MARKETS[effectiveMarket];
  const { data: currencyLabels = {} } = useStorefrontCurrencies(effectiveMarketObj.countryCode, marketReady);

  // نُبلّغ مخزن السلّة بجاهزيّة الكشف والسوق الحقيقيّ، لئلّا تُنشأ سلّة
  // بسوق افتراضيّ (AE) ثمّ تُفرَغ على عميل السعوديّة/عمان/العراق.
  React.useEffect(() => {
    // أثناء جلسة إهداء نمرّر null فيبقى سوق المستلم الذي يرسله المكوّن.
    setCartMarketContext(
      giftMarketOverride ? null : (market.countryCode as CountryCode),
      marketReady,
    );
  }, [market.countryCode, marketReady, giftMarketOverride]);



  const value: LocaleContextValue = {
    locale,
    marketId,
    countryCode: market.countryCode,
    currency: currencyLabels[market.countryCode] ?? "",
    currencyLabels,
    marketSource,
    marketReady,
    showDetectionBanner,
    dismissDetectionBanner,
    detectedMarketId,
    showMismatchBanner:
      !!detectedMarketId &&
      detectedMarketId !== marketId &&
      !giftMarketOverride &&
      marketSource === "auto" &&
      mismatchDismissedFor !== detectedMarketId,
    dismissMismatchBanner: React.useCallback(() => {
      if (!detectedMarketId) return;
      setMismatchDismissedFor(detectedMarketId);
      try {
        window.localStorage.setItem(MISMATCH_DISMISS_KEY, detectedMarketId);
      } catch {
        /* ignore */
      }
    }, [detectedMarketId]),
    setLocale: switchLocale,
    setMarket,
    resetMarketToAuto,
    giftMarketOverride,
    setGiftMarketOverride,
    clearGiftMarketOverride,
    effectiveMarket,
    effectiveCurrency: currencyLabels[effectiveMarketObj.countryCode] ?? "",
    effectiveCountryCode: effectiveMarketObj.countryCode,
    t: (key, vars) => t(locale, key, vars),
    formatPrice: (amount, currencyCode) => formatMoney(amount, currencyCode, locale),
    dir: locale === "ar" ? "rtl" : "ltr",
  };

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

export function useLocale() {
  const ctx = React.useContext(LocaleContext);
  if (!ctx) throw new Error("useLocale must be used within LocaleProvider");
  return ctx;
}

/**
 * نسخة صامتة من useLocale — تُستخدم في المكوّنات الجذريّة (مثل AdminCommandRoot)
 * التي قد تُعاد تركيبتها أثناء HMR page-reload قبل أن يستقرّ الـProvider.
 * ترجع قيماً افتراضيّة آمنة بدل رمي خطأ يكسر SSR.
 */
export function useLocaleSafe(): LocaleContextValue {
  const ctx = React.useContext(LocaleContext);
  if (ctx) return ctx;
  return {
    locale: "ar",
    marketId: "AE",
    countryCode: "AE",
    currency: "AED",
    currencyLabels: {},
    marketSource: "auto",
    marketReady: false,
    showDetectionBanner: false,
    dismissDetectionBanner: () => {},
    detectedMarketId: null,
    showMismatchBanner: false,
    dismissMismatchBanner: () => {},
    setLocale: () => {},
    setMarket: () => {},
    resetMarketToAuto: async () => {},
    giftMarketOverride: null,
    setGiftMarketOverride: () => {},
    clearGiftMarketOverride: () => {},
    effectiveMarket: "AE",
    effectiveCurrency: "AED",
    effectiveCountryCode: "AE",
    t: (key: string) => key,
    formatPrice: (amount: string | number, currencyCode: string) => `${amount} ${currencyCode}`,
    dir: "rtl",
  } as unknown as LocaleContextValue;
}
